
import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  Search, 
  Globe, 
  BarChart2, 
  Briefcase, 
  ArrowUpRight, 
  ArrowDownRight,
  Zap,
  Info,
  ShieldCheck,
  AlertTriangle,
  Download
} from 'lucide-react';

interface Stock {
  symbol: string;
  name: string;
  price: number;
  change: number;
  isin: string;
  risk: 'Low' | 'Medium' | 'High';
  sentiment: number;
}

export const MarketView: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const [stocks, setStocks] = useState<Stock[]>([
    { symbol: 'AAPL', name: 'Apple Inc.', price: 178.42, change: 1.2, isin: 'US0378331005', risk: 'Low', sentiment: 82 },
    { symbol: 'MSFT', name: 'Microsoft Corp', price: 330.15, change: -0.4, isin: 'US5949181045', risk: 'Low', sentiment: 75 },
    { symbol: 'TSLA', name: 'Tesla, Inc.', price: 214.50, change: 4.8, isin: 'US88160R1014', risk: 'High', sentiment: 45 },
    { symbol: 'AMZN', name: 'Amazon.com Inc.', price: 125.80, change: -1.5, isin: 'US0231351067', risk: 'Medium', sentiment: 60 },
  ]);

  const [query, setQuery] = useState('');

  // Simulate real-time price updates
  useEffect(() => {
    const interval = setInterval(() => {
      setStocks(prev => prev.map(s => ({
        ...s,
        price: +(s.price + (Math.random() - 0.5) * 2).toFixed(2),
        change: +(s.change + (Math.random() - 0.5) * 0.1).toFixed(1)
      })));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="p-6 space-y-8 animate-in slide-in-from-right duration-500 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center space-x-4">
          <button onClick={onBack} className="p-3 bg-white border border-slate-200 rounded-2xl hover:bg-slate-100 transition-all text-slate-600 shadow-sm">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Market Integrity Monitor</h1>
            <p className="text-sm text-slate-500 font-medium">Real-time ISIN validation, volatility tracking & insider risk scoring.</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-white border border-slate-200 text-slate-700 p-3 rounded-2xl shadow-sm hover:bg-slate-50"><Download size={20} /></button>
          <button className="bg-blue-600 text-white px-6 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all active:scale-95">
             Sync Bloomberg Terminal
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
               <div className="flex items-center space-x-2 bg-white px-4 py-2.5 rounded-2xl border border-slate-200 w-full md:w-96 shadow-inner focus-within:ring-4 focus-within:ring-blue-500/10 transition-all">
                  <Search size={16} className="text-slate-400" />
                  <input 
                    type="text" 
                    placeholder="Search by ISIN or Symbol..." 
                    className="text-sm outline-none w-full font-medium" 
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
               </div>
               <div className="hidden sm:flex items-center space-x-2">
                 <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                 <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">NYSE: OPEN</span>
               </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50/80 text-[10px] uppercase tracking-widest font-black text-slate-400 border-b border-slate-100">
                  <tr>
                    <th className="px-8 py-5">Ticker</th>
                    <th className="px-8 py-5">ISIN Identity</th>
                    <th className="px-8 py-5">Price</th>
                    <th className="px-8 py-5">24h Change</th>
                    <th className="px-8 py-5">Integrity</th>
                    <th className="px-8 py-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {stocks.map((stock) => (
                    <tr key={stock.symbol} className="hover:bg-slate-50 transition-all group cursor-pointer">
                      <td className="px-8 py-5">
                         <div className="flex items-center space-x-3">
                           <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-black text-xs">
                             {stock.symbol}
                           </div>
                           <span className="text-sm font-black text-slate-900">{stock.name}</span>
                         </div>
                      </td>
                      <td className="px-8 py-5 font-mono text-xs text-slate-500">{stock.isin}</td>
                      <td className="px-8 py-5 text-sm font-black text-slate-900">${stock.price}</td>
                      <td className="px-8 py-5">
                         <div className={`flex items-center space-x-1 text-sm font-black ${stock.change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                           {stock.change >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                           <span>{Math.abs(stock.change)}%</span>
                         </div>
                      </td>
                      <td className="px-8 py-5">
                         <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${
                           stock.risk === 'Low' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 
                           stock.risk === 'Medium' ? 'bg-amber-50 text-amber-700 border-amber-100' : 
                           'bg-rose-50 text-rose-700 border-rose-100'
                         }`}>
                           {stock.risk} RISK
                         </span>
                      </td>
                      <td className="px-8 py-5 text-right">
                         <button className="p-2 text-slate-400 hover:text-blue-600 transition-colors">
                            <Activity size={18} />
                         </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white shadow-2xl relative overflow-hidden group">
             <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center">
                <AlertTriangle size={16} className="mr-2 text-amber-500" /> Market Volatility Alert
             </h3>
             <p className="text-sm font-medium leading-relaxed opacity-80 mb-6">
               Unusual options activity detected on TSLA (isin: ...1014). Outliers suggest potential leaked corporate news in the Singapore region.
             </p>
             <div className="flex items-center justify-between border-t border-white/10 pt-6">
                <div>
                   <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Internal Risk</p>
                   <p className="text-xl font-black text-rose-400">Critical</p>
                </div>
                <button className="p-3 bg-white/10 rounded-xl hover:bg-white/20 transition-all">
                   <ShieldCheck size={20} className="text-emerald-400" />
                </button>
             </div>
             <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600 rounded-full blur-[60px] opacity-10 translate-x-1/2 -translate-y-1/2"></div>
          </div>

          <div className="bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-sm">
             <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-6">Market Sentiment Distribution</h3>
             <div className="space-y-4">
                {[
                  { label: 'Bullish Signals', p: 68, color: 'bg-emerald-500' },
                  { label: 'Neutral/Sideways', p: 22, color: 'bg-slate-300' },
                  { label: 'Bearish Outliers', p: 10, color: 'bg-rose-500' },
                ].map((s, i) => (
                  <div key={i} className="space-y-1.5">
                    <div className="flex justify-between text-[10px] font-black uppercase">
                      <span className="text-slate-500">{s.label}</span>
                      <span className="text-slate-900">{s.p}%</span>
                    </div>
                    <div className="w-full bg-slate-50 h-1.5 rounded-full overflow-hidden">
                       <div className={`${s.color} h-full transition-all duration-1000`} style={{ width: `${s.p}%` }}></div>
                    </div>
                  </div>
                ))}
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};
